import {
  Avatar,
  Box,
  Button,
  Flex,
  Grid,
  GridItem,
  Heading,
  Link as ChakraLink,
  Text,
  VStack,
  useDisclosure,
} from "@chakra-ui/react";
import { MdChevronRight } from "react-icons/md";
import { BookOpen, Flame, Play, Video } from "lucide-react";
import QRCode from "qrcode.react";
import React, { useEffect, useMemo, useState } from "react";
import { withTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import AnnouncementsCarousel from "../../components/student/AnnouncementsCarousel";
import DailyChallengeMenu from "../../components/student/DailyChallengeMenu";
import HomeAnalyticsCharts from "../../components/student/HomeAnalyticsCharts";
import MissedQuestions from "../../components/student/Cards/MissedQuestions";
import RecentContentPanel from "../../components/student/RecentContentPanel";
import StudentPortalShell from "../../components/student/StudentPortalShell";
import UpcomingOfficeHoursPanel from "../../components/student/UpcomingOfficeHoursPanel";
import { getPerformanceAnalyticsRequest } from "../../modules/students-modules/performance/Actions";
import { getAllSectionsRequest } from "../../modules/students-modules/sections/Actions";
import {
  getLiveVideoRequest,
  setLiveVideoSeenRequest,
} from "../../modules/students-modules/videos/Actions";
import { getActiveAnnouncementsRequest } from "../../modules/students-modules/announcements/Actions";
import { studentPalette } from "../../constants/studentPalette";
import { studentSurfaces } from "../../constants/studentThemeSurfaces";

const C = {
  bg: studentSurfaces.canvas,
  primary: studentPalette.primary,
  secondary: studentPalette.secondary,
  border: studentSurfaces.border,
  cream: studentPalette.cream,
  paper: studentSurfaces.raised,
  warmBeige: studentPalette.warmBeige,
  muted: studentSurfaces.textMuted,
};

function scanAttendanceUrl(userId) {
  const raw = "https://edutopiaonline.com/";
  const base = String(raw)
    .replace(/\s/g, "")
    .replace(/['"]/g, "")
    .replace(/\/$/, "");
  return `${base}/scan-code/${userId}/attendence`;
}

const StudentHome = ({ t }) => {
  const baseUrl = "https://api.edutopiaonline.com/";
  const dispatch = useDispatch();
  const userData = useSelector((state) => state.user.userData);
  const liveVideo = useSelector((state) => state.chapterVideos.liveVideo);
  const announcements = useSelector((state) => state.studentAnnouncements.announcements);
  const sections = useSelector((state) => state.sections.sections);
  const performanceData = useSelector((state) => state.performance.analytics);
  const perfLoading = useSelector((state) => state.performance.isLoading);

  const [showAnnouncements, setShowAnnouncements] = useState(false);
  const { isOpen: isChallengeOpen, onOpen: onChallengeOpen, onClose: onChallengeClose } = useDisclosure();

  useEffect(() => {
    dispatch(getAllSectionsRequest());
    dispatch(getLiveVideoRequest());
    dispatch(getActiveAnnouncementsRequest());
  }, [dispatch]);

  useEffect(() => {
    if (userData?.id) {
      dispatch(getPerformanceAnalyticsRequest(userData.id));
    }
  }, [dispatch, userData?.id]);

  useEffect(() => {
    const hasSeenAnnouncements = localStorage.getItem("hasSeenAnnouncements");
    if (!hasSeenAnnouncements && announcements && announcements.length > 0) {
      setShowAnnouncements(true);
    }
  }, [announcements]);

  const handleCloseAnnouncements = () => {
    setShowAnnouncements(false);
    localStorage.setItem("hasSeenAnnouncements", "true");
  };

  const courseSections = useMemo(() => {
    const raw = sections;
    const list = Array.isArray(raw)
      ? raw
      : Array.isArray(raw?.data)
        ? raw.data
        : [];
    const subjectId = userData?.subject?.id;
    return list.filter((s) => {
      if (subjectId == null || subjectId === undefined) return true;
      if (s.subject_id == null || s.subject_id === undefined) return true;
      return Number(s.subject_id) === Number(subjectId);
    });
  }, [sections, userData?.subject?.id]);

  return (
    <StudentPortalShell>
      <Box
        bg={C.bg}
        minH={{ base: "auto", md: "100vh" }}
        py={{ base: 6, md: 10 }}
        px={{ base: 4, md: 6, lg: 8 }}
        overflowX="hidden"
        w="100%"
        maxW="100%"
        minW={0}
      >
        <Box w="100%" maxW="100%" minW={0}>
          {/* Top: dossier + recent */}
          <Grid templateColumns={{ base: "1fr", lg: "2fr 1fr" }} gap={6} mb={8} w="full" minW={0}>
            <GridItem minW={0}>
              <VStack align="stretch" spacing={4}>
                <Flex
                  position="relative"
                  direction={{ base: "column", sm: "row" }}
                  align={{ base: "stretch", sm: "center" }}
                  justify="space-between"
                  gap={6}
                  p={{ base: 6, md: 8 }}
                  bg={C.paper}
                  borderWidth="1px"
                  borderColor={C.border}
                  borderRadius="lg"
                  boxShadow="sm"
                  w="full"
                  minW={0}
                >
                {userData?.grade?.id === 10 && (
                  <Box position="absolute" top="-10px" left="-10px" zIndex={1}>
                    <Text
                      bg="yellow.400"
                      color="white"
                      fontWeight="800"
                      px={3}
                      py={1}
                      borderRadius="md"
                      fontSize="xs"
                      letterSpacing="0.1em"
                      textTransform="uppercase"
                      boxShadow="md"
                    >
                      Premium
                    </Text>
                  </Box>
                )}
                <Flex align="flex-start" gap={{ base: 4, md: 8 }} flex="1" minW={0}>
                  <Avatar
                    size="xl"
                    borderRadius="lg"
                    src={userData?.subject?.logo ? baseUrl + userData.subject.logo.replace(/^\//, "") : undefined}
                    name={userData?.name}
                  />
                  <Box minW={0}>
                    <Text
                      fontSize="10px"
                      fontWeight="800"
                      letterSpacing="0.2em"
                      color={`${C.secondary}99`}
                      textTransform="uppercase"
                      mb={1}
                    >
                      Student dossier
                    </Text>
                    <Heading
                      size="lg"
                      color={C.primary}
                      fontWeight="800"
                      mb={4}
                      fontFamily="'Tajawal','Amiri',sans-serif"
                      maxW="100%"
                      wordBreak="break-word"
                      sx={{ "html[data-theme='dark'] &": { color: "var(--text-primary)" } }}
                    >
                      {userData?.name}
                    </Heading>
                    <Flex gap={10} flexWrap="wrap">
                      <Box>
                        <Text fontSize="9px" fontWeight="700" color={`${C.secondary}66`} textTransform="uppercase" letterSpacing="wider">
                          Group
                        </Text>
                        <Text fontSize="sm" fontWeight="700" color={C.secondary}>
                          {userData?.group?.name || "—"}
                        </Text>
                      </Box>
                      <Box>
                        <Text fontSize="9px" fontWeight="700" color={`${C.secondary}66`} textTransform="uppercase" letterSpacing="wider">
                          Subject
                        </Text>
                        <Text fontSize="sm" fontWeight="700" color={C.secondary}>
                          {userData?.subject?.name || "—"}
                        </Text>
                      </Box>
                    </Flex>
                  </Box>
                </Flex>
                <VStack
                  spacing={2}
                  p={4}
                  bg={studentSurfaces.muted}
                  borderWidth="1px"
                  borderColor={C.border}
                  borderRadius="lg"
                  align="center"
                  minW={{ base: "full", sm: "140px" }}
                  w={{ base: "full", sm: "auto" }}
                >
                  <Box
                    bg={C.paper}
                    p={2}
                    borderRadius="md"
                    borderWidth="1px"
                    borderColor={C.border}
                  >
                    <QRCode size={112} value={scanAttendanceUrl(userData?.id)} />
                  </Box>
                  <Text fontSize="9px" fontWeight="800" color={`${C.secondary}99`} textTransform="uppercase" letterSpacing="widest">
                    Attendance QR
                  </Text>
                </VStack>
              </Flex>
                <UpcomingOfficeHoursPanel />
              </VStack>
            </GridItem>

            <GridItem minW={0} display="flex" flexDirection="column">
              <RecentContentPanel performanceData={performanceData} isLoading={perfLoading} />
            </GridItem>
          </Grid>

          {/* Analytics dashboard */}
          <Box mb={10} w="full" minW={0}>
            <Box mb={6}>
              <Text
                fontSize="sm"
                fontWeight="800"
                color={C.secondary}
                letterSpacing="0.08em"
                textTransform="uppercase"
                sx={{ "html[data-theme='dark'] &": { color: "var(--text-muted)" } }}
              >
                Learning insights
              </Text>
              <Text fontSize="sm" color={C.muted} mt={1}>
                Track your accuracy, monthly progress, and chapters that need the most review.
              </Text>
            </Box>
            <HomeAnalyticsCharts
              performanceData={performanceData}
              isLoading={perfLoading}
              sessionsSlot={
                <Box
                  p={6}
                  bg={C.paper}
                  borderWidth="1px"
                  borderColor={C.border}
                  borderRadius="lg"
                  w="full"
                  minW={0}
                  flex="1"
                  display="flex"
                  flexDirection="column"
                  justifyContent="space-between"
                >
                  <Box>
                    <Text
                      fontSize="10px"
                      fontWeight="800"
                      letterSpacing="0.14em"
                      color={C.muted}
                      textTransform="uppercase"
                      mb={3}
                    >
                      {courseSections.length === 0 ? "Current modules" : "Continue learning"}
                    </Text>
                    {courseSections.length === 0 ? (
                      <Text fontSize="sm" color={C.muted} lineHeight="tall">
                        Your enrolled courses will show here. Open a course to get started.
                      </Text>
                    ) : (
                      <Box
                        className="scroll"
                        maxH="220px"
                        overflowY="auto"
                        overflowX="hidden"
                        pr={1}
                        css={{
                          scrollbarWidth: "thin",
                          scrollbarColor: "var(--scrollbar-thumb, #5B0D16) transparent",
                        }}
                      >
                      <VStack align="stretch" spacing={3}>
                        {courseSections.map((section) => (
                          <Flex
                            key={section.id}
                            as={Link}
                            to={`/students/courses/${section.id}`}
                            align="center"
                            gap={2}
                            p={3}
                            bg={studentSurfaces.muted}
                            borderWidth="1px"
                            borderColor={C.border}
                            borderRadius="md"
                            _hover={{
                              borderColor: C.primary,
                              bg: "rgba(91, 13, 22, 0.06)",
                              textDecoration: "none",
                            }}
                            transition="border-color 0.2s, background 0.2s"
                          >
                            <Box minW={0} flex="1">
                              <Text
                                fontSize="9px"
                                fontWeight="800"
                                color={C.primary}
                                textTransform="uppercase"
                                letterSpacing="0.08em"
                                mb={1}
                                sx={{ "html[data-theme='dark'] &": { color: "var(--text-primary)" } }}
                              >
                                Course
                              </Text>
                              <Text
                                fontSize="sm"
                                fontWeight="700"
                                color={C.secondary}
                                noOfLines={2}
                                sx={{ "html[data-theme='dark'] &": { color: "var(--text-primary)" } }}
                              >
                                {section.name}
                              </Text>
                            </Box>
                            <Box
                              as="span"
                              display="inline-flex"
                              color={C.primary}
                              flexShrink={0}
                              aria-hidden
                              sx={{ "html[data-theme='dark'] &": { color: "var(--text-primary)" } }}
                            >
                              <MdChevronRight size={22} />
                            </Box>
                          </Flex>
                        ))}
                      </VStack>
                      </Box>
                    )}
                  </Box>
                  <Button
                    as={Link}
                    to="/students/courses"
                    mt={5}
                    w="full"
                    size="sm"
                    bg={C.primary}
                    color={C.cream}
                    fontWeight="800"
                    textTransform="uppercase"
                    fontSize="10px"
                    letterSpacing="0.08em"
                    borderRadius="lg"
                    _hover={{ bg: C.secondary }}
                  >
                    {courseSections.length === 0 ? "Browse courses" : "View all courses"}
                  </Button>
                </Box>
              }
            />
          </Box>

          <Box mb={10} w="full" minW={0}>
            <MissedQuestions />
          </Box>

          <Flex
            pt={8}
            pb={4}
            borderTopWidth="1px"
            borderColor={C.border}
            direction={{ base: "column", md: "row" }}
            justify="space-between"
            gap={6}
            align={{ base: "flex-start", md: "flex-end" }}
          >
            <Box maxW="md">
              <Text fontSize="10px" fontWeight="800" letterSpacing="0.12em" color={`${C.secondary}66`} textTransform="uppercase" mb={2}>
                Edutopia student portal
              </Text>
              <Text fontSize="sm" color={C.muted} lineHeight="tall">
                Your progress and attendance tools in one place.
              </Text>
            </Box>
            <ChakraLink
              as={Link}
              to="/privacy-policy"
              fontSize="9px"
              fontWeight="800"
              letterSpacing="0.08em"
              textTransform="uppercase"
              color={C.secondary}
              _hover={{ color: C.primary }}
            >
              Privacy
            </ChakraLink>
          </Flex>
        </Box>
      </Box>

      {/* <div className="fixed bottom-6 right-6 z-50">
        <button
          type="button"
          onClick={onChallengeOpen}
          className="relative bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white rounded-full p-4 shadow-lg hover:shadow-xl transition-all duration-200 flex items-center justify-center group"
          style={{ width: "64px", height: "64px" }}
          title="Daily Challenge"
        >
          <Flame className="w-8 h-8 group-hover:scale-110 transition-transform" />
          {userData?.streak_count > 0 && (
            <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center">
              {userData.streak_count}
            </span>
          )}
        </button>
      </div> */}

      <AnnouncementsCarousel
        announcements={announcements}
        isOpen={showAnnouncements}
        onClose={handleCloseAnnouncements}
      />
      <DailyChallengeMenu isOpen={isChallengeOpen} onClose={onChallengeClose} userData={userData} />
    </StudentPortalShell>
  );
};

export default withTranslation()(StudentHome);
